import React, { useState, useMemo, useEffect, useRef } from 'react';

import
{
  Modal,
  Input,
  Text,
  Icon,
  FileTypeIcon,
  Checkbox,
  Button,
  FileTypeFilter,
  ProgressBar,
} from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';

import
{
  getFileExtension,
  getFileTypeCategory,
  formatFileSize,
} from '../../../utils/fileUtils';
import { formatArtifactTitle } from '../../../utils/parseArtifacts';
import api from '../../../utils/axios';
import { formatDate } from '../../../utils/dateUtils';

import { Document } from '../../DocumentsPanel/DocumentsPanel.types';
import { FormAlert } from '../../FormAlert/FormAlert';
import { getCABFileInfo } from '../../../services/cabApi';

import { IFileUploadDialogProps } from './FileUpload.type';
import { FILE_INPUT_ACCEPT, IMAGE_EXTENSIONS } from '../../../utils/fileUploadConfig';

// Skeleton Loading Component for File Rows
const FileRowSkeleton: React.FC<{ index: number }> = ({ index }) => {
  return (
    <tr 
      className="border-b border-border-secondary animate-fade-in-up opacity-0"
      style={{ 
        animationDelay: `${index * 0.05}s`,
        animationFillMode: 'forwards'
      }}
    >
      {/* Name Column */}
      <td className='py-bg-v2-md px-bg-v2-lg'>
        <div className='flex items-center gap-bg-v2-md'>
          {/* Checkbox Skeleton */}
          <div className="w-4 h-4 skeleton-shimmer rounded-bg-v2-sm shrink-0"></div>
          
          {/* File Icon Skeleton */}
          <div className="w-8 h-8 skeleton-shimmer rounded-bg-v2-sm shrink-0"></div>
          
          {/* File Info Skeleton */}
          <div className="flex flex-col gap-bg-v2-xs flex-1 min-w-0">
            <div className="h-5 skeleton-shimmer rounded-bg-v2-sm w-3/5"></div>
            <div className="h-4 skeleton-shimmer rounded-bg-v2-sm w-1/4"></div>
          </div>
        </div>
      </td>

      {/* Date Column */}
      <td className='py-bg-v2-md px-bg-v2-lg'>
        <div className="flex items-center justify-center">
          <div className="h-5 skeleton-shimmer rounded-bg-v2-sm w-32"></div>
        </div>
      </td>

      {/* Type Column */}
      <td className='py-bg-v2-md px-bg-v2-lg'>
        <div className="flex items-center justify-center">
          <div className="h-6 skeleton-shimmer rounded-bg-v2-full w-16"></div>
        </div>
      </td>
    </tr>
  );
};

export const FileUploadDialog: React.FC<IFileUploadDialogProps> = ({
  projectId,
  isOpen,
  onClose,
  onUpload,
  onSelect,
  uploading,
  onCancelUpload,
  onCancel,
  allowedDocumentExtensions,
  acceptAnyFileType = false,
  uploadProgress = null,
}) => {
  const restrictTypes = !!(allowedDocumentExtensions && allowedDocumentExtensions.length > 0);
  const isAllowedDocumentName = (name: string) =>
    !restrictTypes ||
    allowedDocumentExtensions!.some(ext => name.toLowerCase().endsWith(ext.toLowerCase()));
  const [loading, setLoading] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<FileTypeFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [documents, setDocuments] = useState<Document[]>([]);
  const [sortBy, setSortBy] = useState<'name' | 'date' | 'type'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedDocuments, setSelectedDocuments] = useState<Document[]>([]);
  const [dragOverUpload, setDragOverUpload] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState<string>("Uploaded 0 files");
  const [uploadedInThisSession, setUploadedInThisSession] = useState<string[]>([]);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load documents when modal opens and reset state when it closes
  useEffect(() => {
    if (isOpen) {
      loadDocuments();
      setUploadedInThisSession([]); // Reset uploaded tracking when opening
    } else {
      // Reset state when modal closes
      setSelectedDocuments([]);
      setSearchQuery('');
      setSelectedFilter('all');
      setSuccessMessage('');
      setUploadedInThisSession([]);
    }
  }, [isOpen, projectId]);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      // Call all APIs in parallel (CAB status is non-blocking — failure is ignored)
      const [artifactsResponse, filesResponse, imagesResponse, cabStatus] = await Promise.all([
        api.get(`/projects/${projectId}/artifacts/`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        api.get(`/projects/${projectId}/files/`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        api.get(`/projects/${projectId}/images/`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        projectId ? getCABFileInfo(projectId).catch(() => null) : Promise.resolve(null),
      ]);

      // Helper to ensure we always have a valid ISO string for dateModified
      const ensureISO = (val: any) => {
        if (!val) return new Date().toISOString();
        // Preserve existing strings as-is to avoid timezone shifts during re-parsing.
        // For strings: We pass them through exactly as they are. Previously, we were doing 
        // new Date(val).toISOString(). If the backend sent a string like 12:00 without a Z, 
        // the browser would treat it as 12:00 Local, and toISOString() would convert it to 
        // 06:30 UTC (if you are in GMT+5:30). This was the "wrong time" seen previously.
        if (typeof val === 'number') return new Date(val * 1000).toISOString();
        return val;
      };

      // Transform artifacts to Document format (System Generated)
      const artifactDocs: Document[] = (artifactsResponse.data || []).map((artifact: any) => ({
        id: artifact.id || artifact._id,
        name: artifact.title || 'Untitled',
        size: artifact.size || artifact.content?.length || 0,
        dateModified: ensureISO(artifact.updated_at || artifact.created_at),
        type: 'system',
        fileType: artifact.type || getFileExtension(artifact.title || 'document.txt'),
        content: artifact.content,
        version: artifact.version,
        source: 'artifact',
      }));

      // Transform files to Document format (User Uploaded)
      const fileDocs: Document[] = (filesResponse.data || []).map((file: any) => ({
        id: file.id || file._id,
        name: file.original_name || file.filename || 'Unknown File',
        size: file.size || 0,
        dateModified: ensureISO(file.uploaded_at || file.created_at || file.uploadedAt),
        type: 'user',
        fileType: getFileExtension(file.original_name || file.filename || ''),
        source: 'file',
      }));

       // Transform files to Document format (User Uploaded)
       const images: Document[] = (imagesResponse.data || []).map((file: any) => ({
        id: file.id || file._id,
        name: file.original_name || file.filename || 'Unknown File',
        size: file.size || 0,
        dateModified: ensureISO(file.uploaded_at || file.created_at || file.uploadedAt),
        type: 'user',
        fileType: getFileExtension(file.original_name || file.filename || ''),
        source: 'image',
      }));

      // Add CAB.md as a system document if it is ready
      const cabDocs: Document[] = [];
      if (cabStatus?.is_ready) {
        const cabDate = ensureISO(cabStatus.completed_at);

        cabDocs.push({
          id: `cab_markdown_${projectId}`,
          name: 'CAB.md',
          size: cabStatus.cab_md_size || 0,
          dateModified: cabDate,
          type: 'system',
          fileType: 'md',
          source: 'cab_markdown',
        });
      }

      setDocuments([...artifactDocs, ...fileDocs, ...images, ...cabDocs]);
    } catch (error) {
      console.error('Failed to load documents:', error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter documents based on active tab, search, and file type filter
  const filteredDocuments = useMemo(() => {
    let filtered = documents;

    // Hide documents outside the allowlist (images are always allowed)
    if (restrictTypes) {
      filtered = filtered.filter(doc => doc.source === 'image' || isAllowedDocumentName(doc.name));
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(doc => 
        doc.name.toLowerCase().includes(query)
      );
    }

    // Filter by file type
    if (selectedFilter !== 'all') {
      filtered = filtered.filter(doc => {
        const category = getFileTypeCategory(doc.fileType);
        if (selectedFilter === 'word') return category === 'word';
        if (selectedFilter === 'excel') return category === 'excel';
        if (selectedFilter === 'powerpoint') return category === 'powerpoint';
        if (selectedFilter === 'pdf') return category === 'pdf';
        if (selectedFilter === 'images') return category === 'image';
        if (selectedFilter === 'others') return !['word', 'excel', 'powerpoint', 'pdf', 'image'].includes(category);
        return true;
      });
    }

    

    // 2. Sort documents (using a copy to avoid mutating state)
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;
      
      if (sortBy === 'name') {
        comparison = a.name.localeCompare(b.name);
      } else if (sortBy === 'date') {
        // Robust date parsing for sort: matches formatDate utility logic
        const parse = (val: string | number) => {
          if (!val) return 0;
          if (typeof val === 'number') return val * 1000;
          
          let dateString = val;
          // If the string doesn't have timezone info, treat it as UTC (matching dateUtils.ts)
          if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/) && !dateString.match(/Z|[+-]\d{2}:\d{2}$/)) {
            dateString = dateString.replace(/(\.\d+)?$/, '') + 'Z';
          }
          const d = new Date(dateString);
          return isNaN(d.getTime()) ? 0 : d.getTime();
        };

        const timeA = parse(a.dateModified);
        const timeB = parse(b.dateModified);
        comparison = timeA - timeB;
      } else if (sortBy === 'type') {
        comparison = a.type.localeCompare(b.type);
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [documents, searchQuery, selectedFilter, sortBy, sortOrder, allowedDocumentExtensions]);

  const handleSort = (column: 'name' | 'date' | 'type') => {
      if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
      } else {
      setSortBy(column);
      setSortOrder('desc');
      }
  };

  const handleUploadAreaDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverUpload(false);
    
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      setUploadingFiles(Array.from(droppedFiles).map(f => f.name));
      const uploadedFiles = await onUpload(Array.from(droppedFiles));
      await loadDocuments();
      setUploadingFiles([]);
      
      if (uploadedFiles.length > 0) {
        setSelectedDocuments(uploadedFiles);
        setUploadedInThisSession(prev => [...prev, ...uploadedFiles.map(f => f.id)]);
        setSuccessMessage(`Successfully uploaded ${uploadedFiles.length} ${uploadedFiles.length === 1 ? 'file' : 'files'}`);
      }
    }
  };

  const toggleFileSelection = (document: Document) => {
    setSelectedDocuments(prevDocuments => 
      prevDocuments.find(selectedDoc => selectedDoc.id === document.id)
      ? prevDocuments.filter(selectedDoc => selectedDoc.id !== document.id)
      : [...prevDocuments, document]
    );
  };

  const handleCancel = () => {
    // Reset selected documents
    setSelectedDocuments([]);
    
    // Cancel ongoing upload if any
    if (uploading && onCancelUpload) {
      onCancelUpload();
    }
    
    // Reset uploading files list
    setUploadingFiles([]);
    
    // Notify parent to remove uploaded documents from this session
    if (onCancel && uploadedInThisSession.length > 0) {
      onCancel(uploadedInThisSession);
    }
    
    // Reset uploaded tracking
    setUploadedInThisSession([]);
    
    // Close the modal
    onClose();
  };

    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        className="!max-w-6xl !h-[80vh] !max-h-[80vh]"
        hideFooter={true}
        title={
          <div className="flex items-center gap-bg-v2-xs">
            <Text variant="text-lg" weight="semibold" color="primary" as="span">Add file</Text>
            <Text variant="text-sm" weight="regular" color="secondary" as="span">(Select from the list or upload new file)</Text>
          </div>
        }>
        <div className="flex gap-bg-v2-xl h-full">
          {/* Left Column - File List */}
          <div className="flex flex-col flex-1 min-w-0">
            <div className="bg-bg-primary border border-border-secondary rounded-bg-v2-md shadow-bg-v2-xs flex flex-col h-full overflow-hidden">
              {/* Search Bar */}
              <div className="border-b border-border-secondary p-bg-v2-lg flex-shrink-0">
                <Input 
                  placeholder="Search"
                  value={searchQuery}
                  className='!rounded-bg-v2-full !w-1/2'
                  onChange={(e) => setSearchQuery(e.target.value)}
                  startIcon={<Icon name={IconName.Search} size="sm" variant="secondary" />}
                />
              </div>

              {/* Filter Buttons */}
              <div className="border-b border-border-secondary p-bg-v2-xl flex flex-col gap-bg-v2-lg flex-shrink-0">
                <div className="flex gap-bg-v2-md flex-wrap">
                  {[
                    { key: 'word', label: 'Word', extension: 'docx', exts: ['.doc', '.docx'] },
                    { key: 'excel', label: 'Excel', extension: 'xlsx', exts: ['.xls', '.xlsx', '.csv'] },
                    { key: 'powerpoint', label: 'PowerPoint', extension: 'pptx', exts: ['.ppt', '.pptx'] },
                    { key: 'pdf', label: 'PDF', extension: 'pdf', exts: ['.pdf'] },
                    { key: 'images', label: 'Images', extension: 'png', exts: null },   // always shown
                    { key: 'others', label: 'Others', extension: 'other', exts: [] },   // hidden when restricted
                  ].filter(filter =>
                    !restrictTypes ||
                    filter.exts === null ||
                    filter.exts.some(ext => allowedDocumentExtensions!.some(allowed => allowed.toLowerCase() === ext))
                  ).map(filter => (
                    <button
                      key={filter.key}
                      onClick={() => setSelectedFilter(selectedFilter === filter.key ? 'all' : filter.key as FileTypeFilter)}
                      className={`
                        border rounded-bg-v2-full px-bg-v2-md py-bg-v2-sm
                        flex items-center gap-bg-v2-md
                        text-bg-v2-sm font-bg-v2-medium
                        transition-colors
                        ${selectedFilter === filter.key
                          ? 'bg-bg-quaternary border-border-primary text-text-primary'
                          : 'bg-bg-primary border-border-secondary text-text-quaternary hover:bg-bg-secondary'
                        }
                      `}>
                      <FileTypeIcon extension={filter.extension} size={16} />{filter.label}
                    </button>
                  ))}
                </div>
                <Text variant="text-sm" color="primary">
                  Total {filteredDocuments.length} {filteredDocuments.length === 1 ? 'file' : 'files'}
                </Text>
              </div>
              
              {/* Table Container */}
              <div className="flex-1 overflow-auto min-h-0 modern-scrollbar">
                <table className="w-full table-fixed">
                  <colgroup>
                    <col style={{ width: '55%' }} />
                    <col style={{ width: '25%' }} />
                    <col style={{ width: '20%' }} />
                  </colgroup>
                  <thead className="sticky top-0 bg-bg-primary z-10">
                    <tr className='font-bg-v2-sans text-text-tertiary text-bg-v2-sm border-b border-border-secondary'>
                      <th className='text-left py-bg-v2-md px-bg-v2-lg'>
                        <div className='flex items-center gap-bg-v2-xs cursor-pointer' onClick={() => handleSort('name')}>
                          <Text variant="text-sm" weight="medium" color="tertiary">Name</Text>
                          <Icon name={IconName.Sort} size="sm" />
                        </div>
                      </th>
                      <th className='py-bg-v2-md px-bg-v2-lg'>
                        <div className='flex items-center justify-center gap-bg-v2-xs cursor-pointer' onClick={() => handleSort('date')}>
                          <Text variant="text-sm" weight="medium" color="tertiary">Date Modified</Text>
                          <Icon name={IconName.Sort} size="sm" />
                        </div>
                      </th>
                      <th className='py-bg-v2-md px-bg-v2-lg'>
                        <div className='flex items-center justify-center gap-bg-v2-xs cursor-pointer' onClick={() => handleSort('type')}>
                          <Text variant="text-sm" weight="medium" color="tertiary">Type</Text>
                          <Icon name={IconName.Sort} size="sm" />
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                  {loading ? (
                    <>
                      {[0, 1, 2, 3, 4, 5].map((index) => (
                        <FileRowSkeleton key={index} index={index} />
                      ))}
                    </>
                  ) : filteredDocuments.length === 0 ? (
                    <tr>
                      <td colSpan={3} className='py-bg-v2-md px-bg-v2-lg text-center items-center justify-center'>
                        <Text variant="text-sm" color="tertiary">No files found</Text>
                      </td>
                    </tr>
                  ) : (
                    filteredDocuments.map((doc, index) => {
                      const isSelected = selectedDocuments.find(selectedDoc => selectedDoc.id === doc.id) ? true : false;
                      return (
                        <tr 
                          key={`file-doc-${index}`}
                          className={`
                            border-b border-border-secondary
                            ${isSelected
                              ? 'bg-bg-tertiary hover:bg-bg-quaternary'
                              : 'bg-bg-primary hover:bg-bg-secondary'
                            }
                            transition-colors
                            cursor-pointer
                            animate-fade-in-up opacity-0
                          `}
                          style={{
                            animationDelay: `${index * 0.05}s`,
                            animationFillMode: 'forwards'
                          }}
                          onClick={() => toggleFileSelection(doc)}
                        >
                          <td className='py-bg-v2-md px-bg-v2-lg'>
                            <div className='flex items-center gap-bg-v2-md'>
                              <div onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                  size="sm"
                                  checked={isSelected}
                                  onChange={() => toggleFileSelection(doc)}
                                  variant="solid"
                                  containerClassName="!w-auto !flex-row !items-center"
                                />
                              </div>
                              <div className='w-8 h-8 flex items-center justify-center flex-shrink-0'>
                                <FileTypeIcon extension={doc.fileType} size={32} />
                              </div>
                              <div className='flex flex-col min-w-0 flex-1'>
                                <Text variant="text-sm" color="primary" className="truncate">{formatArtifactTitle(doc.name, doc.version)}</Text>
                                {doc.size > 0 && (
                                  <Text variant="text-xs" color="quaternary">{formatFileSize(doc.size)}</Text>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className='py-bg-v2-md px-bg-v2-lg'>
                            <div className="flex items-center justify-center">
                              <Text variant="text-sm" color="quaternary">{formatDate(doc.dateModified)}</Text>
                            </div>
                          </td>
                          <td className='py-bg-v2-md px-bg-v2-lg'>
                            <div className="flex items-center justify-center">
                              <div className={`
                                inline-flex items-center px-bg-v2-md py-bg-v2-xxs rounded-bg-v2-full border
                                ${doc.type === 'user' 
                                  ? 'bg-utility-brand-50 border-utility-brand-200 text-utility-brand-700' 
                                  : 'bg-utility-orange-50 border-utility-orange-200 text-utility-orange-700'
                                }
                              `}>
                                <Text variant="text-xs" weight="medium" className="capitalize">{doc.type}</Text>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Right Column - Upload Area */}
          <div className="flex flex-col flex-shrink-0">
            <div className="flex-1 mb-bg-v2-2xl relative">
              <div
                className={`
                  border border-dashed rounded-bg-v2-md
                  flex flex-col items-center justify-center
                  p-bg-v2-2xl h-full
                  transition-colors
                  ${dragOverUpload 
                    ? 'border-border-brand bg-bg-brand-primary' 
                    : 'border-alpha-black-20 bg-bg-primary'
                  }
                  `}
                  onDragEnter={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDragOverUpload(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (e.currentTarget === e.target) {
                    setDragOverUpload(false);
                  }
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDrop={handleUploadAreaDrop}
                >
                {successMessage && (
                  <FormAlert
                    className="m-bg-v2-sm absolute top-0 left-0"
                    autoDismiss={true}
                    autoDismissTimeout={5000}
                    message={successMessage}
                    variant="success"
                    onDismiss={() => setSuccessMessage('')} />
                )}
                <div className="flex flex-col items-center gap-bg-v2-lg text-center px-bg-v2-md">
                  <Text variant="text-sm" weight="medium" color="placeholder" className="leading-bg-v2-text-sm">
                    Drag & drop image or file                  
                  </Text>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={acceptAnyFileType ? undefined : restrictTypes ? [...IMAGE_EXTENSIONS, ...allowedDocumentExtensions!].join(',') : FILE_INPUT_ACCEPT}
                    onChange={async (e) => {
                      const files = e.target.files;
                      if (files && files.length > 0) {
                        setUploadingFiles(Array.from(files).map(f => f.name));
                        const uploadedFiles = await onUpload(Array.from(files));
                        await loadDocuments();
                        setUploadingFiles([]);
                        
                        if (uploadedFiles.length > 0) {
                          setSelectedDocuments(uploadedFiles);
                          setUploadedInThisSession(prev => [...prev, ...uploadedFiles.map(f => f.id)]);
                          setSuccessMessage(`Successfully uploaded ${uploadedFiles.length} ${uploadedFiles.length === 1 ? 'file' : 'files'}`);
                        }
                        
                        // Reset input value to allow re-uploading the same file
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                      }
                    }}
                    className="hidden"
                  />
                  <Button
                    size="medium"
                    label={
                      <div className="flex items-center gap-bg-v2-xs">
                        <Icon name={IconName.Upload} size="md" />
                        <Text variant="text-sm" weight="semibold" color="tertiary">Upload file</Text>
                      </div>
                    }
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="bg-bg-primary border-border-primary shadow-bg-v2-xs"
                  />
                </div>
              </div>

              {/* Upload Progress Overlay */}
              {uploading && uploadingFiles.length > 0 && (
                <div className="absolute inset-0 bg-bg-primary border-2 border-border-brand rounded-bg-v2-md flex items-center justify-center z-20">
                  <div className="flex flex-col items-center gap-bg-v2-lg text-center px-bg-v2-md w-full max-w-full">
                    {uploadProgress === null ? (
                      <div className="animate-spin rounded-full w-bg-v2-2xl h-bg-v2-2xl border-2 border-t-transparent border-fg-brand-primary"></div>
                    ) : (
                      <div className="flex flex-col items-center gap-bg-v2-xs w-full">
                        <ProgressBar value={uploadProgress} label="Uploading files" />
                        <Text variant="text-xs" weight="medium" color="tertiary">
                          {Math.round(uploadProgress)}%
                        </Text>
                      </div>
                    )}
                    <div className="flex flex-col gap-bg-v2-xs">
                      <span className="text-bg-v2-sm font-bg-v2-semibold text-text-primary">
                        Uploading in progress...
                      </span>
                      <div className="flex flex-col gap-bg-v2-xxs mt-bg-v2-sm">
                        {uploadingFiles.map((fileName, index) => (
                          <span 
                            key={index} 
                            className="text-bg-v2-xs font-bg-v2-regular text-text-tertiary truncate max-w-full"
                          >
                            {fileName}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer Buttons */}
            <div className="flex items-center justify-end gap-bg-v2-md">
              <Button
                label="Cancel"
                size="small"
                variant="outline"
                onClick={handleCancel}
                className="bg-bg-primary border-border-primary shadow-bg-v2-xs"
              />
              <Button
                label={`Add file (${selectedDocuments.length}/${documents.length})`}
                variant={"primary"}
                size="small"
                onClick={() => {
                  console.log('🔍 [FileUploadDialog] Selected Documents:', selectedDocuments);
                  selectedDocuments.forEach((doc, idx) => {
                    console.log(`🔍 [FileUploadDialog] Doc ${idx}:`, {
                      id: doc.id,
                      name: doc.name,
                      source: doc.source,
                      type: doc.type,
                      fileType: doc.fileType
                    });
                  });
                  onSelect(selectedDocuments);
                }}
                disabled={selectedDocuments.length === 0}
              />
            </div>
          </div>
        </div>
      </Modal>
    );
  };